import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Share2, Palette, ClipboardList, Video, Calendar, CheckCircle, ArrowRight, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SERVICE_CONFIG = {
  digital_marketing: {
    icon: TrendingUp,
    color: '#7D468C',
    gradient: 'from-[#7D468C]/20 to-[#7D468C]/5',
    bgAccent: '#7D468C',
  },
  social_media: {
    icon: Share2,
    color: '#4EBFBF',
    gradient: 'from-[#4EBFBF]/20 to-[#4EBFBF]/5',
    bgAccent: '#4EBFBF',
  },
  branding: {
    icon: Palette,
    color: '#F2785C',
    gradient: 'from-[#F2785C]/20 to-[#F2785C]/5',
    bgAccent: '#F2785C',
  },
  project_management: {
    icon: ClipboardList,
    color: '#7D468C',
    gradient: 'from-[#7D468C]/20 to-[#7D468C]/5',
    bgAccent: '#7D468C',
  },
  content_creation: {
    icon: Video,
    color: '#4EBFBF',
    gradient: 'from-[#4EBFBF]/20 to-[#4EBFBF]/5',
    bgAccent: '#4EBFBF',
  },
  event_production: {
    icon: Calendar,
    color: '#F2785C',
    gradient: 'from-[#F2785C]/20 to-[#F2785C]/5',
    bgAccent: '#F2785C',
  },
};

const VALID_SERVICES = Object.keys(SERVICE_CONFIG);

const ServicePage = () => {
  const { serviceKey } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Validate service key
  if (!VALID_SERVICES.includes(serviceKey)) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0F1C2E] flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="font-poppins font-bold text-3xl text-[#233559] dark:text-white mb-4">
              Service not found
            </h1>
            <Link
              to="/#servicios"
              className="inline-flex items-center gap-2 text-[#7D468C] hover:underline font-semibold"
            >
              ← Back to Services
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const config = SERVICE_CONFIG[serviceKey];
  const Icon = config.icon;
  const data = t(`service_page.${serviceKey}`, { returnObjects: true });
  const includes = t(`service_page.${serviceKey}.includes`, { returnObjects: true });
  const forList = t(`service_page.${serviceKey}.for`, { returnObjects: true });

  return (
    <div className="min-h-screen bg-white dark:bg-[#0F1C2E] flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero Section */}
        <section className={`bg-gradient-to-br ${config.gradient} dark:from-[#1A2639] dark:to-[#0F1C2E] pt-24 pb-20 px-4`}>
          <div className="max-w-5xl mx-auto">
            {/* Back link */}
            <Link
              to="/#servicios"
              className="inline-flex items-center gap-1 text-sm font-semibold mb-8 transition-opacity hover:opacity-70 font-source-sans"
              style={{ color: config.color }}
            >
              {t('service_page.back')}
            </Link>

            <div className="flex flex-col md:flex-row md:items-start gap-8">
              {/* Icon */}
              <div
                className="flex-shrink-0 w-20 h-20 flex items-center justify-center"
                style={{ backgroundColor: config.color + '20' }}
              >
                <Icon size={36} style={{ color: config.color }} />
              </div>

              <div>
                {/* Tag */}
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-2.5 h-2.5"
                    style={{ transform: 'rotate(45deg)', backgroundColor: config.color }}
                  />
                  <span
                    className="text-xs font-bold tracking-widest uppercase font-poppins"
                    style={{ color: config.color }}
                  >
                    {data.hero_tag}
                  </span>
                </div>

                {/* Headline */}
                <h1 className="font-poppins font-bold text-3xl sm:text-4xl lg:text-5xl text-[#233559] dark:text-white leading-tight mb-5">
                  {data.headline}
                </h1>

                {/* Subheadline */}
                <p className="text-lg text-[#233559]/65 dark:text-white/60 leading-relaxed max-w-2xl font-source-sans">
                  {data.subheadline}
                </p>

                {/* CTA button */}
                <Link
                  to="/contacto"
                  className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 font-poppins font-semibold text-white text-sm tracking-wide transition-opacity hover:opacity-90"
                  style={{ backgroundColor: config.color }}
                >
                  {t('service_page.cta_button')}
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included + Who It's For */}
        <section className="py-20 px-4 bg-white dark:bg-[#0F1C2E]">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* What's Included */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-3 h-3"
                  style={{ transform: 'rotate(45deg)', backgroundColor: config.color }}
                />
                <h2 className="font-poppins font-bold text-2xl text-[#233559] dark:text-white">
                  {t('service_page.includes_title')}
                </h2>
              </div>

              <ul className="space-y-4">
                {Array.isArray(includes) && includes.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle
                      size={20}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: config.color }}
                    />
                    <span className="text-[#233559]/75 dark:text-white/65 font-source-sans leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Who It's For */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="w-3 h-3"
                  style={{ transform: 'rotate(45deg)', backgroundColor: config.color }}
                />
                <h2 className="font-poppins font-bold text-2xl text-[#233559] dark:text-white">
                  {t('service_page.for_title')}
                </h2>
              </div>

              <ul className="space-y-4">
                {Array.isArray(forList) && forList.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Users
                      size={20}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: config.color }}
                    />
                    <span className="text-[#233559]/75 dark:text-white/65 font-source-sans leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section
          className="py-20 px-4"
          style={{ backgroundColor: config.color }}
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-white mb-4">
              {t('service_page.cta_title')}
            </h2>
            <p className="text-white/80 text-lg mb-8 font-source-sans">
              {t('service_page.cta_subtitle')}
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white font-poppins font-bold text-sm tracking-wide transition-opacity hover:opacity-90"
              style={{ color: config.color }}
            >
              {t('service_page.cta_button')}
              <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ServicePage;
